/**
 * NavigationList, pure half.
 *
 * Moved verbatim from components/FlowBuilder/flow-components/components/NavigationListComponent.js. Runs unchanged in the
 * browser and, via the flow-core bundle, inside GraalJS on the backend, so it
 * must never reach for React, the DOM, Node APIs, or the network.
 */

import { reservedFieldNameErrors } from '../fieldName';

export const NavigationListCore = {
  type: 'NavigationList',
  name: 'Navigation List',
  icon: '📋',
  description: 'A list of tappable rows that navigate to other screens.',
  category: 'input',
  maxPerScreen: 2,
  getDefaultProperties() {
    return {
      name: 'navigation_list',
      label: '',
      description: '',
      'media-size': 'regular',
      'list-items': [
        {
          id: 'item_1',
          'main-content': {
            title: 'Item 1',
            description: 'Description for item 1',
          },
          'on-click-action': {
            name: 'navigate',
            next: { type: 'screen', name: '' },
          },
        },
        {
          id: 'item_2',
          'main-content': {
            title: 'Item 2',
            description: 'Description for item 2',
          },
          'on-click-action': {
            name: 'navigate',
            next: { type: 'screen', name: '' },
          },
        },
      ],
      visible: true,
    };
  },
  getPropertySchema() {
    return {
      name: {
        type: 'string',
        label: 'Field Name',
        required: true,
      },
      label: {
        type: 'string',
        label: 'Label',
        required: false,
      },
      description: {
        type: 'string',
        label: 'Description',
        required: false,
      },
      'media-size': {
        type: 'select',
        label: 'Media Size',
        required: false,
        options: ['regular', 'large'],
      },
      'list-items': {
        type: 'array',
        label: 'List Items',
        required: true,
      },
      visible: {
        type: 'boolean',
        label: 'Visible',
        required: false,
      },
    };
  },
  validateProperties(properties) {
    var errors = [];
    var warnings = [];

    if (!properties.name || properties.name.trim().length === 0) {
      errors.push('Field name is required');
    }

    if (properties.name && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(properties.name)) {
      errors.push('Field name must start with letter or underscore');
    }

    errors.push(...reservedFieldNameErrors(properties.name));

    if (!properties['list-items'] || properties['list-items'].length === 0) {
      errors.push('At least one list item is required');
    }

    if (properties['list-items'] && properties['list-items'].length > 20) {
      errors.push('Maximum 20 list items allowed');
    }

    if (properties['list-items']) {
      var hasBadge = false;
      var ids = new Set();

      properties['list-items'].forEach((item, index) => {
        if (!item.id) {
          errors.push(`Item ${index + 1} is missing an ID`);
        } else if (ids.has(item.id)) {
          errors.push(`Duplicate item ID: ${item.id}`);
        } else {
          ids.add(item.id);
        }

        if (!item['main-content'] || !item['main-content'].title) {
          errors.push(`Item ${index + 1} is missing a title`);
        }

        if (item.badge) {
          if (hasBadge) {
            errors.push('Only one item can have a badge per list');
          }
          hasBadge = true;
        }

        if (item.tags && item.tags.length > 3) {
          errors.push(`Item ${index + 1} can have maximum 3 tags`);
        }

        const action = item['on-click-action'];
        if (!action || !action.name) {
          errors.push(`Item ${index + 1} is missing an action`);
        } else if (action.name === 'navigate' && !action.next?.name) {
          // Same rule as the Footer: a navigate action must target a screen, else WhatsApp rejects
          // it as "Required property 'next' is missing." Catch it locally for instant feedback.
          errors.push(`Item ${index + 1} navigates but has no target screen`);
        }
      });
    }

    // The "max 2 per screen" limit is enforced by buildValidationMap (maxPerScreen) when actually
    // exceeded; no always-on warning here, which fired even for a single NavigationList.

    return { errors, warnings };
  },
  transformToWhatsApp(properties) {
    var result = {
      type: 'NavigationList',
      name: properties.name,
      'list-items': (properties['list-items'] || []).map((item) => {
        var listItem = {
          id: item.id,
          'main-content': {
            title: item['main-content']?.title,
          },
        };

        if (item['main-content']?.description) {
          listItem['main-content'].description = item['main-content'].description;
        }

        if (item['main-content']?.metadata) {
          listItem['main-content'].metadata = item['main-content'].metadata;
        }

        if (item.end && (item.end.title || item.end.description || item.end.metadata)) {
          listItem.end = {};
          if (item.end.title) listItem.end.title = item.end.title;
          if (item.end.description) listItem.end.description = item.end.description;
          if (item.end.metadata) listItem.end.metadata = item.end.metadata;
        }

        if (item.badge) {
          listItem.badge = item.badge;
        }

        if (item.tags && item.tags.length > 0) {
          listItem.tags = item.tags;
        }

        // on-click-action is required for NavigationList items
        if (item['on-click-action']) {
          var action = { ...item['on-click-action'] };
          // If navigate action has empty next.name, fall back to complete action
          if (action.name === 'navigate' && action.next) {
            if (!action.next.name || action.next.name.trim() === '') {
              listItem['on-click-action'] = { name: 'complete' };
            } else {
              listItem['on-click-action'] = action;
            }
          } else {
            listItem['on-click-action'] = action;
          }
        } else {
          // Default to complete action if no action specified
          listItem['on-click-action'] = { name: 'complete' };
        }

        return listItem;
      }),
    };

    if (properties.label) {
      result.label = properties.label;
    }

    if (properties.description) {
      result.description = properties.description;
    }

    if (properties['media-size'] && properties['media-size'] !== 'regular') {
      result['media-size'] = properties['media-size'];
    }

    if (properties.visible === false) {
      result.visible = false;
    }

    return result;
  },
  transformFromWhatsApp(whatsappComponent) {
    var label = whatsappComponent.label;
    if (typeof label === 'object' && label !== null) {
      label = label.text || '';
    }

    var description = whatsappComponent.description;
    if (typeof description === 'object' && description !== null) {
      description = description.text || '';
    }

    return {
      name: whatsappComponent.name,
      label: label || '',
      description: description || '',
      'media-size': whatsappComponent['media-size'] || 'regular',
      'list-items': (whatsappComponent['list-items'] || []).map((item) => ({
        id: item.id,
        'main-content': {
          title: item['main-content']?.title || '',
          description: item['main-content']?.description || '',
          metadata: item['main-content']?.metadata || '',
        },
        end: item.end ? {
          title: item.end.title || '',
          description: item.end.description || '',
          metadata: item.end.metadata || '',
        } : undefined,
        badge: item.badge || '',
        tags: item.tags || [],
        'on-click-action': item['on-click-action'] || {
          name: 'navigate',
          next: { type: 'screen', name: '' },
        },
      })),
      visible: whatsappComponent.visible !== false,
    };
  },
};
